import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { type Outreach } from '@libraries/typescript/api_client';

/**
 * Human-readable prefix for the outreach link label on a voice call,
 * based on the linked outreach's type. Falls back to "Outreach" when the
 * type is missing or unrecognized.
 */
export function getVoiceCallOutreachLabelPrefix(
    outreachType: Outreach['outreach_type'] | null | undefined,
): string {
    switch (outreachType) {
        case 'clock_in':
            return 'Clock In Reminder';
        case 'clock_out':
            return 'Clock Out Reminder';
        case 'shift_confirmation':
            return 'Shift Confirmation';
        case 'recurring_shift':
            return 'Recurring Shift';
        case 'survey':
            return 'Survey';
        case 'message':
            return 'Message';
        default:
            return 'Outreach';
    }
}

export function formatVoiceCallOutreachClientNames(
    outreachClientNames: ReadonlyArray<string> | null | undefined,
): string | null {
    const normalizedNames = (outreachClientNames ?? [])
        .map((name) => name.trim())
        .filter((name) => name.length > 0);

    if (normalizedNames.length === 0) {
        return null;
    }

    return normalizedNames.join(', ');
}

/**
 * Build the human-readable label for the outreach link shown on outbound
 * voice calls. The prefix reflects the linked outreach's type (e.g.
 * "Outreach", "Clock In Reminder", "Clock Out Reminder"), combined with the
 * linked outreach's client name(s), the earliest shift start time, and a
 * "+N more" indicator when the outreach covers multiple shifts.
 *
 * Always returns a usable string so callers can render the link
 * unconditionally; falls back to "View {type}" (e.g. "View outreach",
 * "View clock in reminder") when no enrichment data is available.
 */
export function formatVoiceCallOutreachLinkLabel({
    outreachType,
    outreachClientNames,
    earliestShiftStartTime,
    shiftCount,
    timezone,
}: {
    readonly outreachType: Outreach['outreach_type'] | null | undefined;
    readonly outreachClientNames: ReadonlyArray<string> | null | undefined;
    readonly earliestShiftStartTime: string | null | undefined;
    readonly shiftCount: number | null | undefined;
    readonly timezone: string;
}): string {
    const prefix = getVoiceCallOutreachLabelPrefix(outreachType);
    const clientNamesText =
        formatVoiceCallOutreachClientNames(outreachClientNames);
    const dateText = earliestShiftStartTime
        ? formatDateAndTime(earliestShiftStartTime, timezone)
        : null;
    const extraShiftCount =
        shiftCount !== null && shiftCount !== undefined && shiftCount > 1
            ? shiftCount - 1
            : 0;
    const moreText = extraShiftCount > 0 ? ` (+${extraShiftCount} more)` : '';

    if (clientNamesText && dateText) {
        return `${prefix} for ${clientNamesText} · ${dateText}${moreText}`;
    }
    if (clientNamesText) {
        return `${prefix} for ${clientNamesText}${moreText}`;
    }
    if (dateText) {
        return `${prefix} · ${dateText}${moreText}`;
    }
    return `View ${prefix.toLowerCase()}`;
}
